"use client";

import { useState } from "react";
import { sendPublicContactMessage } from "@/lib/public-contact";

export function ContactForm() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<{
    kind: "error" | "success";
    message: string;
  } | null>(null);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const form = event.currentTarget;
    const formData = new FormData(form);

    const payload = {
      name: String(formData.get("name") ?? "").trim(),
      email: String(formData.get("email") ?? "").trim(),
      company: String(formData.get("company") ?? "").trim(),
      message: String(formData.get("message") ?? "").trim(),
    };

    if (!payload.name || !payload.email || !payload.message) {
      setStatus({
        kind: "error",
        message: "Please fill in your name, email, and message.",
      });
      return;
    }

    setIsSubmitting(true);
    setStatus(null);

    try {
      const response = await sendPublicContactMessage(payload);
      form.reset();
      setStatus({
        kind: "success",
        message: response.message ?? "Thanks, your message has been sent.",
      });
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : "Could not send your message. Please try again.";

      setStatus({ kind: "error", message });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="mt-6 grid gap-4" onSubmit={handleSubmit}>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="grid gap-2 text-sm font-medium text-slate-900">
          Name
          <input
            className="h-11 rounded-full border border-slate-300 bg-white px-4 text-sm text-slate-900 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-indigo-300 focus-visible:outline-none"
            name="name"
            placeholder="Jane Doe"
            type="text"
            autoComplete="name"
            required
          />
        </label>

        <label className="grid gap-2 text-sm font-medium text-slate-900">
          Work email
          <input
            className="h-11 rounded-full border border-slate-300 bg-white px-4 text-sm text-slate-900 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-indigo-300 focus-visible:outline-none"
            name="email"
            placeholder="you@company.com"
            type="email"
            autoComplete="email"
            inputMode="email"
            required
          />
        </label>
      </div>

      <label className="grid gap-2 text-sm font-medium text-slate-900">
        Company
        <input
          className="h-11 rounded-full border border-slate-300 bg-white px-4 text-sm text-slate-900 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-indigo-300 focus-visible:outline-none"
          name="company"
          placeholder="Your company"
          type="text"
          autoComplete="organization"
        />
      </label>

      <label className="grid gap-2 text-sm font-medium text-slate-900">
        Message
        <textarea
          className="h-24 resize-none rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus-visible:ring-2 focus-visible:ring-indigo-300 focus-visible:outline-none"
          name="message"
          placeholder="How can we help?"
          autoComplete="off"
          required
        />
      </label>

      <button
        className="mt-1 inline-flex h-11 items-center justify-center rounded-full bg-indigo-600 px-4 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-70 focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:outline-none"
        type="submit"
        disabled={isSubmitting}
      >
        {isSubmitting ? "Sending…" : "Send message"}
      </button>

      <p
        aria-live="polite"
        className={`min-h-5 text-sm ${
          status?.kind === "error" ? "text-red-700" : "text-emerald-700"
        }`}
      >
        {status?.message}
      </p>
    </form>
  );
}
